import type { Feature, Geometry } from 'geojson';
import L from 'leaflet';
import { locationInsideGeometry } from './geometryFilter';
import { LocationPopup } from './locationPopup';
import { buildPopupContent } from './popupDom';
import { buildSearchBlob } from './searchText';
import type { MapLocation, MapPayload } from './types';

const POLAND_CENTER: L.LatLngExpression = [52.1, 19.3];
const DEFAULT_ZOOM = 5.5;
const MAP_ZOOM_STEP = 0.5;

/**
 * Leaflet `minZoom` — how far the user may zoom out (max „oddalenie”).
 * Must stay a fixed floor: deriving it from `getZoom()` after animated `fitBounds`
 * captured the current zoom (e.g. Warsaw) and blocked returning to Poland.
 * Kept below the zoom `fitBounds` often needs on narrow mobile viewports so Poland
 * is never clipped at the sides.
 */
const MAP_MIN_ZOOM = DEFAULT_ZOOM;

/** Inactive voivodeship — white at ~50% over map green (reads as light green tint). */
const BASE_STYLE: L.PathOptions = {
  color: '#2a2a2a',
  weight: 1,
  fillColor: '#ffffff',
  fillOpacity: 0.5,
};

/** Active voivodeship — solid white, distinct from inactive regions. */
const SELECTED_STYLE: L.PathOptions = {
  color: '#2a2a2a',
  weight: 1,
  fillColor: '#ffffff',
  fillOpacity: 1,
};

/** Hover on non-selected regions — slightly stronger than base inactive fill. */
const HOVER_STYLE: L.PathOptions = {
  color: '#2a2a2a',
  weight: 1,
  fillColor: '#ffffff',
  fillOpacity: 0.68,
};

/** Hover while selected — keep solid white, emphasize border. */
const HOVER_SELECTED_STYLE: L.PathOptions = {
  color: '#1a4d1a',
  weight: 1.5,
  fillColor: '#ffffff',
  fillOpacity: 1,
};

/** City outline (warszawa.geojson) at voivodeship zoom — single multipolygon, readable stroke. */
const WARSAW_OUTLINE_STYLE: L.PathOptions = {
  color: '#355535',
  weight: 2,
  fillColor: '#e8f2e8',
  fillOpacity: 0.38,
};

/** District polygons (warszawa-dzielnice.geojson) when zoomed in. */
const WARSAW_DISTRICTS_STYLE: L.PathOptions = {
  color: '#3d5a3d',
  weight: 1.5,
  fillColor: '#eaf6ea',
  fillOpacity: 0.42,
};

/**
 * From this zoom upward show dzielnice instead of the simple outline.
 * Outline click uses the same value as fitBounds maxZoom so one action crosses the threshold.
 */
const WARSAW_DISTRICT_DETAIL_MIN_ZOOM = 11;

/** Click a district polygon — closer than outline, still not extreme. */
const WARSAW_DISTRICT_CLICK_MAX_ZOOM = 12;

function prefersReducedMotion(): boolean {
  if (typeof globalThis.matchMedia !== 'function') {
    return false;
  }
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function groupKey(loc: MapLocation): string {
  const { lat, lon } = loc.map_data;
  return `${lat.toFixed(6)},${lon.toFixed(6)}`;
}

function groupLocations(list: MapLocation[]): Map<string, MapLocation[]> {
  const g = new Map<string, MapLocation[]>();
  for (const loc of list) {
    const k = groupKey(loc);
    const arr = g.get(k) ?? [];
    arr.push(loc);
    g.set(k, arr);
  }
  return g;
}

function locationDisplayName(loc: MapLocation): string {
  return loc.name || loc.map_data.label || 'Lokalizacja';
}

function buildMarkerAccessibleName(locs: MapLocation[]): string {
  if (locs.length === 1) {
    return `Pokaż szczegóły: ${locationDisplayName(locs[0]!)}`;
  }
  return `Pokaż szczegóły: ${locs.length} lokalizacje w tym miejscu`;
}

function applyMarkerAccessibleName(marker: L.Marker, locs: MapLocation[]): void {
  const label = buildMarkerAccessibleName(locs);
  const setLabel = (): void => {
    marker.getElement()?.setAttribute('aria-label', label);
  };
  marker.on('add', setLabel);
  setLabel();
}

const markerIcon = L.divIcon({
  className: 'kfx-map-marker',
  html: '<span class="kfx-map-marker__dot" aria-hidden="true"></span>',
  iconSize: [28, 36],
  iconAnchor: [14, 36],
});

function eachPathLayer(layer: L.Layer, fn: (path: L.Path) => void): void {
  if (layer instanceof L.Path) {
    fn(layer);
    return;
  }
  if (layer instanceof L.FeatureGroup) {
    layer.eachLayer((sub) => {
      eachPathLayer(sub, fn);
    });
  }
}

type VoivodeshipChoice = {
  id: number;
  nazwa: string;
  layer: L.Layer;
  geometry: Geometry;
};

function layerLatLngBounds(layer: L.Layer): L.LatLngBounds | null {
  if (!('getBounds' in layer) || typeof layer.getBounds !== 'function') {
    return null;
  }
  const b = layer.getBounds() as L.LatLngBounds;
  return b.isValid() ? b : null;
}

/** Extra padding on the axis that is tighter in the map container (mobile portrait clips lon). */
function polandFitPadding(map: L.Map): L.PointExpression {
  const size = map.getSize();
  const base = 12;
  if (size.x <= 0 || size.y <= 0) {
    return [base, base];
  }
  const aspect = size.x / size.y;
  if (aspect < 0.9) {
    return [base, base + 16];
  }
  if (aspect > 1.35) {
    return [base + 16, base];
  }
  return [base + 8, base + 8];
}

function ensureBoundsFullyVisible(map: L.Map, bounds: L.LatLngBounds): void {
  let guard = 0;
  while (!map.getBounds().contains(bounds) && map.getZoom() > MAP_MIN_ZOOM && guard < 8) {
    map.setZoom(map.getZoom() - MAP_ZOOM_STEP, { animate: false });
    guard += 1;
  }
}

/** After a safe fit, zoom in by half-steps while Poland stays fully in view. */
function nudgePolandViewCloser(map: L.Map, bounds: L.LatLngBounds, maxNudge = 1): void {
  let nudged = 0;
  while (nudged < maxNudge) {
    const current = map.getZoom();
    map.setZoom(current + MAP_ZOOM_STEP, { animate: false });
    if (!map.getBounds().contains(bounds)) {
      map.setZoom(current, { animate: false });
      return;
    }
    nudged += MAP_ZOOM_STEP;
  }
}

export function initInteractiveMap(root: HTMLElement): void {
  const rawPayload = root.getAttribute('data-locations');
  const geoUrl = root.getAttribute('data-geojson-url');
  const warsawOutlineGeoUrl = root.getAttribute('data-warszawa-geojson-url');
  const warsawDistrictsGeoUrl = root.getAttribute('data-warszawa-dzielnice-geojson-url');
  if (!rawPayload || !geoUrl) {
    return;
  }

  let payload: MapPayload;
  try {
    payload = JSON.parse(rawPayload) as MapPayload;
  } catch {
    return;
  }

  if (!payload.locations) {
    payload.locations = [];
  }
  if (!payload.categories) {
    payload.categories = [];
  }

  root.classList.add('kfx-interactive-map--ready');
  root.replaceChildren();
  if (root.id === '') {
    root.id = `kfx-interactive-map-${Date.now().toString(36)}`;
  }

  const layout = document.createElement('div');
  layout.className = 'kfx-interactive-map__layout';

  const toolbar = document.createElement('div');
  toolbar.className = 'kfx-interactive-map__toolbar';

  const searchWrap = document.createElement('div');
  searchWrap.className = 'kfx-interactive-map__search-wrap';

  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'kfx-interactive-map__search';
  searchInput.setAttribute('autocomplete', 'off');
  searchInput.setAttribute('spellcheck', 'false');
  const searchId = `${root.id}-search`;
  searchInput.id = searchId;

  const searchLabel = document.createElement('label');
  searchLabel.className = 'kfx-interactive-map__label';
  searchLabel.setAttribute('for', searchId);
  searchLabel.textContent = 'Szukaj na mapie';

  const suggestions = document.createElement('ul');
  suggestions.className = 'kfx-interactive-map__suggestions';
  suggestions.hidden = true;
  suggestions.setAttribute('role', 'listbox');
  const listboxId = `${root.id}-suggestions`;
  suggestions.id = listboxId;

  searchInput.setAttribute('role', 'combobox');
  searchInput.setAttribute('aria-autocomplete', 'list');
  searchInput.setAttribute('aria-haspopup', 'listbox');
  searchInput.setAttribute('aria-controls', listboxId);
  searchInput.setAttribute('aria-expanded', 'false');

  searchWrap.append(searchLabel, searchInput, suggestions);

  let categorySelect: HTMLSelectElement | null = null;
  if (payload.showCategoryFilter && payload.categories.length > 0) {
    categorySelect = document.createElement('select');
    categorySelect.className = 'kfx-interactive-map__category';
    const catId = `${root.id}-category`;
    categorySelect.id = catId;
    const catLabel = document.createElement('label');
    catLabel.className = 'kfx-interactive-map__label';
    catLabel.setAttribute('for', catId);
    catLabel.textContent = 'Kategoria';
    const optAll = document.createElement('option');
    optAll.value = '';
    optAll.textContent = 'Wszystkie kategorie';
    categorySelect.appendChild(optAll);
    for (const cat of payload.categories) {
      const opt = document.createElement('option');
      opt.value = cat;
      opt.textContent = cat;
      categorySelect.appendChild(opt);
    }
    const catField = document.createElement('div');
    catField.className = 'kfx-interactive-map__field';
    catField.append(catLabel, categorySelect);
    toolbar.append(searchWrap, catField);
  } else {
    toolbar.appendChild(searchWrap);
  }

  const voivodeshipSelect = document.createElement('select');
  voivodeshipSelect.className = 'kfx-interactive-map__category';
  const voiId = `${root.id}-voivodeship`;
  voivodeshipSelect.id = voiId;
  const voiLabel = document.createElement('label');
  voiLabel.className = 'kfx-interactive-map__label';
  voiLabel.setAttribute('for', voiId);
  voiLabel.textContent = 'Województwo';
  const voiOptAll = document.createElement('option');
  voiOptAll.value = '';
  voiOptAll.textContent = 'Wszystkie województwa';
  voivodeshipSelect.appendChild(voiOptAll);
  const voiField = document.createElement('div');
  voiField.className = 'kfx-interactive-map__field';
  voiField.append(voiLabel, voivodeshipSelect);
  toolbar.appendChild(voiField);

  const mapEl = document.createElement('div');
  mapEl.className = 'kfx-interactive-map__map';

  layout.append(toolbar, mapEl);
  root.appendChild(layout);

  const map = L.map(mapEl, {
    zoomControl: true,
    attributionControl: false,
    zoomSnap: MAP_ZOOM_STEP,
    zoomDelta: MAP_ZOOM_STEP,
  });
  map.setView(POLAND_CENTER, DEFAULT_ZOOM, { animate: !prefersReducedMotion() });
  map.setMinZoom(MAP_MIN_ZOOM);

  const locationPopup = new LocationPopup({ root, map, mapEl });

  const markersLayer = L.layerGroup().addTo(map);
  const markerByKey = new Map<string, L.Marker>();

  let suggestionMatches: MapLocation[] = [];
  let activeSuggestionIndex = -1;

  const searchIndex = new Map<MapLocation, string>();
  for (const loc of payload.locations) {
    searchIndex.set(loc, buildSearchBlob(loc));
  }

  let selectedVoivodeshipGeometry: Geometry | null = null;
  let selectedVoivodeshipLayer: L.Layer | null = null;
  let selectedVoivodeshipNazwa: string | null = null;
  let polandBounds: L.LatLngBounds | null = null;

  let geoLayer: L.GeoJSON | null = null;
  let warsawOutlineLayer: L.GeoJSON | null = null;
  let warsawOutlineLoaded = false;
  let warsawOutlineLoading: Promise<void> | null = null;

  let warsawDistrictsLayer: L.GeoJSON | null = null;
  let warsawDistrictsLoaded = false;
  let warsawDistrictsLoading: Promise<void> | null = null;

  function filteredLocationsForDisplay(): MapLocation[] {
    let list = payload.locations;
    const cat = categorySelect?.value ?? '';
    if (cat !== '') {
      list = list.filter((l) => l.category === cat);
    }
    if (selectedVoivodeshipGeometry) {
      list = list.filter((l) => locationInsideGeometry(l, selectedVoivodeshipGeometry));
    }
    return list;
  }

  function hideSuggestions(): void {
    suggestionMatches = [];
    activeSuggestionIndex = -1;
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.removeAttribute('aria-activedescendant');
    suggestions.hidden = true;
    suggestions.replaceChildren();
  }

  function setSuggestionActive(nextIndex: number): void {
    const items = suggestions.querySelectorAll<HTMLElement>('[role="option"]');
    if (items.length === 0) {
      return;
    }
    const clamped = Math.max(0, Math.min(nextIndex, items.length - 1));
    activeSuggestionIndex = clamped;
    items.forEach((el, i) => {
      el.classList.toggle('kfx-interactive-map__suggestion--active', i === clamped);
    });
    const current = items[clamped];
    if (current?.id) {
      searchInput.setAttribute('aria-activedescendant', current.id);
    }
    current?.scrollIntoView({ block: 'nearest' });
  }

  function applySearchPick(loc: MapLocation): void {
    const k = groupKey(loc);
    const marker = markerByKey.get(k);
    if (marker) {
      map.setView(marker.getLatLng(), Math.max(map.getZoom(), 9), { animate: !prefersReducedMotion() });
      const keyLocs = groupLocations(filteredLocationsForDisplay()).get(k) ?? [loc];
      locationPopup.open(buildPopupContent(keyLocs), marker.getLatLng());
    }
    searchInput.value = loc.name || '';
    hideSuggestions();
  }

  function showSuggestions(query: string): void {
    const q = query.trim().toLowerCase();
    suggestions.replaceChildren();
    activeSuggestionIndex = -1;
    searchInput.removeAttribute('aria-activedescendant');
    if (q.length < 2) {
      hideSuggestions();
      return;
    }
    const pool = filteredLocationsForDisplay();
    const matches: MapLocation[] = [];
    for (const loc of pool) {
      const blob = searchIndex.get(loc) ?? '';
      if (blob.includes(q)) {
        matches.push(loc);
      }
      if (matches.length >= 8) {
        break;
      }
    }
    if (matches.length === 0) {
      hideSuggestions();
      return;
    }
    suggestionMatches = matches;
    matches.forEach((loc, idx) => {
      const li = document.createElement('li');
      li.className = 'kfx-interactive-map__suggestion';
      li.setAttribute('role', 'option');
      li.id = `${root.id}-opt-${idx}`;
      const title = document.createElement('span');
      title.className = 'kfx-interactive-map__suggestion-title';
      title.textContent = loc.name || loc.map_data.label || '—';
      const sub = document.createElement('span');
      sub.className = 'kfx-interactive-map__suggestion-sub';
      sub.textContent = loc.map_data.label ?? '';
      li.append(title, sub);
      li.addEventListener('mousedown', (e) => {
        e.preventDefault();
      });
      li.addEventListener('click', () => {
        applySearchPick(loc);
      });
      suggestions.appendChild(li);
    });
    suggestions.hidden = false;
    searchInput.setAttribute('aria-expanded', 'true');
  }

  function refreshMarkers(): void {
    markersLayer.clearLayers();
    markerByKey.clear();
    const groups = groupLocations(filteredLocationsForDisplay());
    for (const [key, locs] of groups) {
      const first = locs[0]!;
      const latlng: L.LatLngExpression = [first.map_data.lat, first.map_data.lon];
      const marker = L.marker(latlng, { icon: markerIcon });
      applyMarkerAccessibleName(marker, locs);
      marker.on('click', (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        locationPopup.open(buildPopupContent(locs), marker.getLatLng());
      });
      marker.addTo(markersLayer);
      markerByKey.set(key, marker);
    }
  }

  function removeWarsawOutlineFromMap(): void {
    if (warsawOutlineLayer && map.hasLayer(warsawOutlineLayer)) {
      map.removeLayer(warsawOutlineLayer);
    }
  }

  function removeWarsawDistrictsFromMap(): void {
    if (warsawDistrictsLayer && map.hasLayer(warsawDistrictsLayer)) {
      map.removeLayer(warsawDistrictsLayer);
    }
  }

  function mazowieckieShowsWarsawDistrictDetail(): boolean {
    return map.getZoom() >= WARSAW_DISTRICT_DETAIL_MIN_ZOOM;
  }

  /**
   * After fitting the Warsaw outline, Leaflet may stay below WARSAW_DISTRICT_DETAIL_MIN_ZOOM when the
   * bounds do not fully fit at that zoom with padding — then district GeoJSON would not swap until the
   * user scrolls again. Nudge zoom to the detail floor and refresh layers; defer update on animated zoom
   * so getZoom() matches the threshold when update runs.
   */
  function ensureWarsawDistrictDetailZoomThenRefreshLayers(allowAnim: boolean): void {
    if (selectedVoivodeshipNazwa !== 'mazowieckie') {
      return;
    }
    if (map.getZoom() < WARSAW_DISTRICT_DETAIL_MIN_ZOOM) {
      map.setZoom(WARSAW_DISTRICT_DETAIL_MIN_ZOOM, { animate: allowAnim });
      if (allowAnim) {
        map.once('zoomend', () => {
          updateMazowieckieWarsawLayers();
        });
        return;
      }
    }
    updateMazowieckieWarsawLayers();
  }

  function loadWarsawOutlineLayerIfNeeded(): void {
    if (!warsawOutlineGeoUrl || warsawOutlineGeoUrl === '') {
      return;
    }
    if (warsawOutlineLayer && map.hasLayer(warsawOutlineLayer)) {
      return;
    }
    if (warsawOutlineLoaded && warsawOutlineLayer && !map.hasLayer(warsawOutlineLayer)) {
      warsawOutlineLayer.addTo(map);
      return;
    }
    if (warsawOutlineLoading) {
      return;
    }
    warsawOutlineLoading = fetch(warsawOutlineGeoUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error(String(res.status));
        }
        return res.json();
      })
      .then((data: unknown) => {
        warsawOutlineLayer = L.geoJSON(data as Parameters<typeof L.geoJSON>[0], {
          style: () => WARSAW_OUTLINE_STYLE,
          interactive: true,
          onEachFeature(_feature, layer) {
            layer.on('click', (e: L.LeafletMouseEvent) => {
              L.DomEvent.stopPropagation(e);
              locationPopup.close();
              const b = layerLatLngBounds(layer);
              if (b) {
                const allowAnim = !prefersReducedMotion();
                map.fitBounds(b, {
                  padding: [32, 32],
                  maxZoom: WARSAW_DISTRICT_DETAIL_MIN_ZOOM,
                  animate: allowAnim,
                });
                if (allowAnim) {
                  map.once('moveend', () => {
                    ensureWarsawDistrictDetailZoomThenRefreshLayers(allowAnim);
                  });
                } else {
                  ensureWarsawDistrictDetailZoomThenRefreshLayers(false);
                }
              }
            });
          },
        });
        warsawOutlineLoaded = true;
        warsawOutlineLoading = null;
        if (selectedVoivodeshipNazwa !== 'mazowieckie') {
          return;
        }
        updateMazowieckieWarsawLayers();
      })
      .catch(() => {
        warsawOutlineLoading = null;
      });
  }

  function loadWarsawDistrictsLayerIfNeeded(): void {
    if (!warsawDistrictsGeoUrl || warsawDistrictsGeoUrl === '') {
      return;
    }
    if (warsawDistrictsLayer && map.hasLayer(warsawDistrictsLayer)) {
      return;
    }
    if (warsawDistrictsLoaded && warsawDistrictsLayer && !map.hasLayer(warsawDistrictsLayer)) {
      warsawDistrictsLayer.addTo(map);
      return;
    }
    if (warsawDistrictsLoading) {
      return;
    }
    warsawDistrictsLoading = fetch(warsawDistrictsGeoUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error(String(res.status));
        }
        return res.json();
      })
      .then((data: unknown) => {
        warsawDistrictsLayer = L.geoJSON(data as Parameters<typeof L.geoJSON>[0], {
          style: () => WARSAW_DISTRICTS_STYLE,
          interactive: true,
          onEachFeature(_feature, layer) {
            layer.on('click', (e: L.LeafletMouseEvent) => {
              L.DomEvent.stopPropagation(e);
              locationPopup.close();
              const b = layerLatLngBounds(layer);
              if (b) {
                map.fitBounds(b, {
                  padding: [24, 24],
                  maxZoom: WARSAW_DISTRICT_CLICK_MAX_ZOOM,
                  animate: !prefersReducedMotion(),
                });
              }
            });
          },
        });
        warsawDistrictsLoaded = true;
        warsawDistrictsLoading = null;
        if (selectedVoivodeshipNazwa !== 'mazowieckie') {
          return;
        }
        updateMazowieckieWarsawLayers();
      })
      .catch(() => {
        warsawDistrictsLoading = null;
      });
  }

  function updateMazowieckieWarsawLayers(): void {
    if (selectedVoivodeshipNazwa !== 'mazowieckie') {
      removeWarsawOutlineFromMap();
      removeWarsawDistrictsFromMap();
      return;
    }

    if (mazowieckieShowsWarsawDistrictDetail()) {
      removeWarsawOutlineFromMap();
      loadWarsawDistrictsLayerIfNeeded();
    } else {
      removeWarsawDistrictsFromMap();
      loadWarsawOutlineLayerIfNeeded();
    }
  }

  function setVoivodeshipSelection(row: VoivodeshipChoice | null): void {
    if (geoLayer && selectedVoivodeshipLayer) {
      geoLayer.resetStyle(selectedVoivodeshipLayer);
    }
    selectedVoivodeshipLayer = row?.layer ?? null;
    selectedVoivodeshipGeometry =
      row?.geometry && (row.geometry.type === 'Polygon' || row.geometry.type === 'MultiPolygon')
        ? row.geometry
        : null;
    selectedVoivodeshipNazwa = row ? row.nazwa.toLowerCase().trim() : null;
    if (geoLayer && selectedVoivodeshipLayer) {
      eachPathLayer(selectedVoivodeshipLayer, (path) => {
        path.setStyle(SELECTED_STYLE);
      });
    }
    updateMazowieckieWarsawLayers();
    refreshMarkers();
  }

  function fitPolandView(animate: boolean): void {
    if (!polandBounds?.isValid()) {
      map.setView(POLAND_CENTER, DEFAULT_ZOOM, { animate });
      map.setMinZoom(MAP_MIN_ZOOM);
      return;
    }
    map.setMinZoom(MAP_MIN_ZOOM);
    map.fitBounds(polandBounds, { padding: polandFitPadding(map), animate });
    const finalize = (): void => {
      ensureBoundsFullyVisible(map, polandBounds!);
      nudgePolandViewCloser(map, polandBounds!);
      map.setMinZoom(MAP_MIN_ZOOM);
    };
    if (animate) {
      map.once('moveend', finalize);
    } else {
      finalize();
    }
  }

  function resetPolandView(): void {
    locationPopup.close();
    searchInput.value = '';
    hideSuggestions();
    setVoivodeshipSelection(null);
    voivodeshipSelect.value = '';
    fitPolandView(!prefersReducedMotion());
  }

  refreshMarkers();
  categorySelect?.addEventListener('change', () => {
    refreshMarkers();
    hideSuggestions();
  });

  let voivodeshipChoices: VoivodeshipChoice[] = [];

  voivodeshipSelect.addEventListener('change', () => {
    hideSuggestions();
    const v = voivodeshipSelect.value;
    if (v === '') {
      resetPolandView();
      return;
    }
    const row = voivodeshipChoices.find((item) => String(item.id) === v);
    if (!row) {
      return;
    }
    locationPopup.close();
    setVoivodeshipSelection(row);
    const bounds = layerLatLngBounds(row.layer);
    if (bounds) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 9, animate: !prefersReducedMotion() });
    }
  });

  map.on('click', () => {
    resetPolandView();
  });

  const AllVoivodeshipsControl = L.Control.extend({
    onAdd() {
      const wrap = L.DomUtil.create('div', 'kfx-interactive-map__all-control leaflet-bar');
      const btn = L.DomUtil.create('button', 'kfx-interactive-map__all-btn', wrap) as HTMLButtonElement;
      btn.type = 'button';
      btn.textContent = 'Wszystkie';
      btn.setAttribute('aria-label', 'Resetuj mapę: cała Polska, wyczyść wyszukiwanie i filtr województwa');
      btn.addEventListener('click', (e) => {
        L.DomEvent.stopPropagation(e);
        e.preventDefault();
        resetPolandView();
      });
      L.DomEvent.disableClickPropagation(wrap);
      return wrap;
    },
  });
  new AllVoivodeshipsControl({ position: 'topright' }).addTo(map);

  map.on('zoomend', updateMazowieckieWarsawLayers);
  map.on('moveend', updateMazowieckieWarsawLayers);

  fetch(geoUrl)
    .then((res) => {
      if (!res.ok) {
        throw new Error(String(res.status));
      }
      return res.json();
    })
    .then((data: unknown) => {
      voivodeshipChoices = [];
      geoLayer = L.geoJSON(data as Parameters<typeof L.geoJSON>[0], {
        style: () => BASE_STYLE,
        onEachFeature(feature: Feature, layer) {
          const geometry = feature.geometry as Geometry | null | undefined;
          const props = feature.properties as { id?: unknown; nazwa?: unknown } | null | undefined;
          const rawId = props?.id;
          const id = typeof rawId === 'number' ? rawId : Number(rawId);
          const nazwa = typeof props?.nazwa === 'string' ? props.nazwa : '';
          let choice: VoivodeshipChoice | null = null;
          if (
            geometry &&
            (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') &&
            Number.isFinite(id) &&
            nazwa !== ''
          ) {
            choice = { id, nazwa, layer, geometry };
            voivodeshipChoices.push(choice);
          }
          layer.on('mouseover', () => {
            eachPathLayer(layer, (path) => {
              path.setStyle(
                layer === selectedVoivodeshipLayer ? HOVER_SELECTED_STYLE : HOVER_STYLE,
              );
            });
          });
          layer.on('mouseout', () => {
            const gl = geoLayer;
            if (!gl) {
              return;
            }
            if (layer === selectedVoivodeshipLayer) {
              eachPathLayer(layer, (path) => {
                path.setStyle(SELECTED_STYLE);
              });
            } else {
              eachPathLayer(layer, (path) => {
                gl.resetStyle(path);
              });
            }
          });
          layer.on('click', (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e);
            if (choice) {
              setVoivodeshipSelection(choice);
            }
            if (Number.isFinite(id)) {
              voivodeshipSelect.value = String(id);
            }
            const bounds = layerLatLngBounds(layer);
            if (bounds) {
              map.fitBounds(bounds, { padding: [24, 24], maxZoom: 9, animate: !prefersReducedMotion() });
            }
          });
        },
      });
      geoLayer.addTo(map);
      voivodeshipChoices.sort((a, b) => a.nazwa.localeCompare(b.nazwa, 'pl'));
      for (const row of voivodeshipChoices) {
        const opt = document.createElement('option');
        opt.value = String(row.id);
        opt.textContent = row.nazwa;
        voivodeshipSelect.appendChild(opt);
      }
      const b = geoLayer.getBounds();
      if (b.isValid()) {
        polandBounds = b;
        map.setMaxBounds(b.pad(0.12));
        map.options.maxBoundsViscosity = 0.85;
        fitPolandView(!prefersReducedMotion());
      }
    })
    .catch(() => {
      map.setView(POLAND_CENTER, DEFAULT_ZOOM, { animate: !prefersReducedMotion() });
      map.setMinZoom(MAP_MIN_ZOOM);
    });

  searchInput.addEventListener('input', () => {
    showSuggestions(searchInput.value);
  });
  searchInput.addEventListener('focus', () => {
    showSuggestions(searchInput.value);
  });
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideSuggestions();
      return;
    }
    if (suggestions.hidden || suggestionMatches.length === 0) {
      return;
    }
    const n = suggestionMatches.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeSuggestionIndex < 0) {
        setSuggestionActive(0);
      } else {
        setSuggestionActive(Math.min(activeSuggestionIndex + 1, n - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeSuggestionIndex <= 0) {
        activeSuggestionIndex = -1;
        suggestions.querySelectorAll('[role="option"]').forEach((el) => {
          el.classList.remove('kfx-interactive-map__suggestion--active');
        });
        searchInput.removeAttribute('aria-activedescendant');
      } else {
        setSuggestionActive(activeSuggestionIndex - 1);
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSuggestionActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setSuggestionActive(n - 1);
    } else if (e.key === 'Enter') {
      if (activeSuggestionIndex >= 0) {
        const loc = suggestionMatches[activeSuggestionIndex];
        if (loc) {
          e.preventDefault();
          applySearchPick(loc);
        }
      }
    }
  });
  root.addEventListener('click', (e) => {
    if (!searchWrap.contains(e.target as Node)) {
      hideSuggestions();
    }
  });

  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          if (selectedVoivodeshipGeometry === null && polandBounds?.isValid()) {
            fitPolandView(false);
          }
        })
      : null;
  resizeObserver?.observe(mapEl);
}
